import React, {useState} from "react";
import {WorkLogEntry} from "../../../API/request/ticket.interface";
import {IntlMessageValue} from "../../../../../@WUM/core/component/const/IntlMessage";
import {dateFormat, MIcon, UniButton} from "../../../../../@WUM/core/Elements";
import {responsibilityApiTYpe} from "../../../../ticketsystem/API/request/responsibility.Api,interface";
import Avatar from "@material-ui/core/Avatar";
import {truncateText} from "../../../../../@WUM/core/Function";
import {WorkLogCommentModal} from "./WorkLogCommentModal";
import {ToastFn} from "../../../../worktimetracking/Pages/components/save/SubmitHandler";

interface TicketTimeTrackingTableProps {
    id?: string;
    Store: any;
    response?: WorkLogEntry[] | boolean | undefined;
    users?: responsibilityApiTYpe[]
    setReload: React.Dispatch<React.SetStateAction<boolean>>
    reload: boolean
    setRecordId: React.Dispatch<React.SetStateAction<string>>
    showToast: ToastFn;
}

export const TicketTimeTrackingTable = (props: TicketTimeTrackingTableProps): JSX.Element => {

    const {id, response, Store, users, setRecordId} = props;
    const MAX_AVATARS: number = 4;

    const [showCommentModal, setShowCommentModal] = useState<boolean>(false);
    const [selectedEntry, setSelectedEntry] = useState<WorkLogEntry | null>(null);

    const handleShowCommentModal = (entry: WorkLogEntry): void => {
        setSelectedEntry(entry);
        setShowCommentModal(true);
    };

    const handleCloseCommentModal = (): void => {
        setShowCommentModal(false);
        setSelectedEntry(null);
    };

    if (id === undefined || !response || typeof response === "boolean" || response.length === 0) {
        return <div className={"mb-3 card card-body"}>Keine Einträge vorhanden</div>;
    }

    let tableBody: JSX.Element[] = [];

    let totalMinutes: number = 0;
    let totalBillableMinutes: number = 0;
    let totalNonBillableMinutes: number = 0;

    const userById: Record<number, responsibilityApiTYpe> = {};
    users?.forEach((user) => {
        userById[user.id] = user;
    });

    if (!response || typeof response === "boolean" || response.length === 0) {
        tableBody.push(
            <tr key="empty">
                <td colSpan={5} className="text-center">empty</td>
            </tr>
        );
    } else {
        response.forEach((entry: WorkLogEntry) => {

            const badge = entry.billable ? (
                <span className="badge bg-success"><MIcon className={"d-flex m-0 fs-6"} children={"check"}/></span>
            ) : (
                <span className="badge bg-secondary"><MIcon className={"d-flex m-0 fs-6"} children={"close"}/></span>
            );

            const user = userById[entry.employeeId];

            const calculatedTime = (entry.totalMinutes / 60).toFixed(2)

            const date = dateFormat(
                entry.workAt,
                localStorage.getItem('locale'),
                'us',
                false,
                "onlyDate",
                Store
            )

            const userAvatar = <Avatar
                alt={user?.username ?? ''}
                title={user?.username ?? ''}
                src={user?.media ?? ''}
                className={'cardAvatarMini'}
            />

            totalMinutes += entry.totalMinutes;

            if (entry.billable) {
                totalBillableMinutes += entry.totalMinutes;
            } else {
                totalNonBillableMinutes += entry.totalMinutes;
            }


            tableBody.push(
                <React.Fragment key={entry.id}>
                    <tr>
                        <td>{userAvatar}</td>
                        <td>{date}</td>
                        <td>{entry.startAt}</td>
                        <td>{entry.stopAt}</td>
                        <td className={"text-end"}>{calculatedTime}</td>
                        <td>{badge}</td>
                        <td>
                            <span className="d-flex justify-content-end gap-1">
                                <UniButton
                                    variant={"outline-secondary"}
                                    class={"fs-6 btn-sm p-0 m-0"}
                                    onClick={(): void => setRecordId(entry.id + "")}
                                >
                                    <MIcon children={"edit"} className={"d-flex"}/>
                                </UniButton>

                                <UniButton
                                    variant={"outline-secondary"}
                                    class={"fs-6 btn-sm p-0 m-0"}
                                    onClick={(): void => handleShowCommentModal(entry)}
                                >
                                    <MIcon children={"comment"} className={"d-flex"}/>
                                </UniButton>
                            </span>
                        </td>
                    </tr>
                    <tr className={""}  onClick={(): void => handleShowCommentModal(entry)}>
                        <td>
                            <span title={"Aktivitätsbericht"}>
                                    <MIcon className={"fs-6 me-2"} children={"article"} style={{cursor: "help"}}/>
                            </span>
                        </td>
                        <td colSpan={6} className={"text-start"}>
                            <span title={entry.activity_report.content} onClick={(): void => handleShowCommentModal(entry)}>
                                {truncateText(entry.activity_report.content, 90)}
                            </span>
                        </td>
                    </tr>
                </React.Fragment>
            )
        });
    }

    const totalHours = (totalMinutes / 60).toFixed(2);
    const totalBillableHours = (totalBillableMinutes / 60).toFixed(2);
    const totalNonBillableHours = (totalNonBillableMinutes / 60).toFixed(2);


    const getSummaryAvatars = (filterCallback: (entry: WorkLogEntry) => boolean): JSX.Element[] => {
        const employeeIds: number[] = Array.from(
            new Set(
                response
                    .filter(filterCallback)
                    .map((entry: WorkLogEntry): number => entry.employeeId)
            )
        );

        const usersFiltered: responsibilityApiTYpe[] = employeeIds
            .map((employeeId: number): responsibilityApiTYpe | undefined => userById[employeeId])
            .filter((user: responsibilityApiTYpe | undefined): user is responsibilityApiTYpe => user !== undefined);

        const visibleUsers: responsibilityApiTYpe[] = usersFiltered.slice(0, MAX_AVATARS);
        const hiddenUsers: responsibilityApiTYpe[] = usersFiltered.slice(MAX_AVATARS);

        const avatars: JSX.Element[] = visibleUsers.map((user: responsibilityApiTYpe): JSX.Element => {
            return (
                <Avatar
                    key={user.id}
                    alt={user.username ?? ''}
                    title={user.username ?? ''}
                    src={user.media ?? ''}
                    className={'cardAvatarMini'}
                />
            );
        });

        if (hiddenUsers.length > 0) {
            const hiddenNames: string = hiddenUsers
                .map((user: responsibilityApiTYpe): string => user.username ?? '')
                .join(', ');

            avatars.push(
                <Avatar
                    key="more"
                    className={'cardAvatarMini bg-secondary text-white justify-align-top'}
                    title={hiddenNames}
                >
                    +{hiddenUsers.length}
                </Avatar>
            );
        }

        return avatars;
    };

    const totalSummaryAvatars = getSummaryAvatars((): boolean => true);
    const billableSummaryAvatars = getSummaryAvatars((entry: WorkLogEntry): boolean => entry.billable);
    const nonBillableSummaryAvatars = getSummaryAvatars((entry: WorkLogEntry): boolean => !entry.billable);
    return (
        <>
            <table className="table table-hover align-middle text-end table-striped">
                <thead className="table-light">
                <tr>
                    <th>{IntlMessageValue("", "common.user", Store)}</th>
                    <th>{IntlMessageValue("", "ticket.work.date", Store)}</th>
                    <th>{IntlMessageValue("", "ticket.work.start", Store)}</th>
                    <th>{IntlMessageValue("", "ticket.work.end", Store)}</th>
                    <th>{IntlMessageValue("", "ticket.work.time", Store)}</th>
                    <th>{IntlMessageValue("", "ticket.work.billable", Store)}</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                {tableBody}
                </tbody>
                <tfoot className="table-light">
                <tr>
                    <td colSpan={4} className="text-end fw-bold">
                        Gesamt:
                    </td>
                    <td className="text-end fw-bold">
                        {totalHours}
                    </td>
                    <td colSpan={2} className="">
                        <span className={"d-flex justify-content-end align-items-center gap-1"}>{totalSummaryAvatars}</span>
                    </td>
                </tr>
                <tr>
                    <td colSpan={4} className="text-end fw-bold">
                        Abrechenbar:
                    </td>
                    <td className="text-end fw-bold">
                        {totalBillableHours}
                    </td>
                    <td colSpan={2} className="">
                        <span
                            className={"d-flex justify-content-end align-items-center gap-1"}>{billableSummaryAvatars}</span>
                    </td>
                </tr>
                <tr>
                    <td colSpan={4} className="text-end fw-bold">
                        Nicht abrechenbar:
                    </td>
                    <td className="text-end fw-bold">
                        {totalNonBillableHours}
                    </td>
                    <td colSpan={3} className="">
                        <span
                            className={"d-flex justify-content-end align-items-center gap-1"}>{nonBillableSummaryAvatars}</span>
                    </td>
                </tr>
                </tfoot>
            </table>
            <WorkLogCommentModal
                entry={selectedEntry}
                show={showCommentModal}
                onClose={handleCloseCommentModal}
                showToast={props.showToast}
                Store={Store}
                users={users}
            />
        </>
    );
};